// BnB tabbed mega menu interactions.
//
// The tabbed mega menu (snippets/header-nav-mega-tabs.liquid) is fetched
// via Shopify's section-rendering API and injected into the DOM after
// page load. Inline <script> tags in that injected HTML don't auto-
// execute, so this file provides equivalent behavior via event
// delegation from the document, which works regardless of when the
// markup appears in the DOM.
//
// Behavior:
// - Hovering a sidebar tab swaps the right-pane content to that tab's
//   panel (mousemove-driven, since some browsers don't fire reliable
//   mouseover/mouseenter events).
// - Each tab is an <a> linking to its corresponding collection / page —
//   clicks navigate away. There's no persistent "active" / "committed"
//   tab; the visual "active" tab is whichever one the cursor is over,
//   defaulting to the first tab on initial render.
//
// Class flags applied to support corner-rounding decisions in CSS:
// - On the active tab: `active-button-top` (if first), `active-button-
//   bottom` (if last and sidebar reaches panel bottom), or `active-
//   button` (middle).
// - On the panels container: `active-columns-top` / `active-columns-
//   bottom` / `active-columns` mirroring the same logic.

(function () {
  'use strict';

  // ---------- panel switching ---------------------------------------

  function showPanelByIndex(root, index) {
    var panels = root.querySelectorAll('.bnb-mm-tabs__panel');
    panels.forEach(function (panel, i) {
      panel.hidden = i !== index;
    });
    updateActiveClasses(root, index);
  }

  // ---------- corner-rounding class flags ---------------------------

  // Returns true when the sidebar's intrinsic content height fills (or
  // exceeds) the panels container's box height.
  //
  // Why we don't use sidebar.scrollHeight: the parent grid uses
  // `align-items: stretch`, so the sidebar's box is stretched to match
  // the panel's height — and scrollHeight reports the stretched box
  // height (not the intrinsic content size) when content is shorter.
  // Sum the tab heights manually for a true intrinsic measurement.
  function sidebarReachesPanelBottom(root) {
    var sidebar = root.querySelector('.bnb-mm-tabs__sidebar');
    var panels = root.querySelector('.bnb-mm-tabs__panels');
    if (!sidebar || !panels) return false;
    var tabs = sidebar.querySelectorAll('.bnb-mm-tabs__tab');
    var contentHeight = 0;
    tabs.forEach(function (tab) {
      contentHeight += tab.offsetHeight;
    });
    return contentHeight >= panels.offsetHeight - 1;
  }

  function updateActiveClasses(root, activeIndex) {
    var sidebar = root.querySelector('.bnb-mm-tabs__sidebar');
    var panels = root.querySelector('.bnb-mm-tabs__panels');
    if (!sidebar || !panels) return;

    var tabs = sidebar.querySelectorAll('.bnb-mm-tabs__tab');
    if (!tabs.length) return;

    // Clear previous flags.
    tabs.forEach(function (tab) {
      tab.classList.remove('active-button', 'active-button-top', 'active-button-bottom');
    });
    panels.classList.remove('active-columns', 'active-columns-top', 'active-columns-bottom');

    var isFirst = activeIndex === 0;
    var isLast = activeIndex === tabs.length - 1;
    var reachesBottom = sidebarReachesPanelBottom(root);

    var tabClass = 'active-button';
    var panelsClass = 'active-columns';
    if (isFirst) {
      tabClass = 'active-button-top';
      panelsClass = 'active-columns-top';
    } else if (isLast && reachesBottom) {
      tabClass = 'active-button-bottom';
      panelsClass = 'active-columns-bottom';
    }

    var activeTab = tabs[activeIndex];
    if (activeTab) activeTab.classList.add(tabClass);
    panels.classList.add(panelsClass);
  }

  // ---------- hover-driven tab switching ----------------------------

  var lastHoverByRoot = new WeakMap();

  document.addEventListener('mousemove', function (e) {
    var sidebar = e.target.closest && e.target.closest('.bnb-mm-tabs__sidebar');
    if (!sidebar) return;

    // Walk tabs and check Y bounds directly rather than using
    // elementFromPoint — disabled buttons used to break that, and even
    // though tabs are now <a> elements, the Y-walk approach is more
    // robust against future style changes.
    var tabs = sidebar.querySelectorAll('.bnb-mm-tabs__tab');
    var foundIndex = -1;
    for (var i = 0; i < tabs.length; i++) {
      var rect = tabs[i].getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        foundIndex = i;
        break;
      }
    }
    if (foundIndex === -1) return;

    var root = sidebar.closest('.bnb-mm-tabs');
    if (!root) return;

    var prevIndex = lastHoverByRoot.get(root);
    lastHoverByRoot.set(root, foundIndex);
    if (prevIndex === foundIndex) {
      // Same tab as last move — but ensure flags are set in case the
      // menu was just opened and we haven't tagged it yet.
      updateActiveClasses(root, foundIndex);
      return;
    }

    showPanelByIndex(root, foundIndex);
  });

  // When the cursor leaves the WHOLE tabbed mega menu (sidebar + panels)
  // — e.g. exiting toward the page background — snap back to the first
  // tab. Don't snap back when the cursor moves from the sidebar into
  // the panels area: the user is reaching for a link in the currently
  // previewed panel, and changing the panel on them would be jarring.
  document.addEventListener('mouseout', function (e) {
    var sidebar = e.target.closest && e.target.closest('.bnb-mm-tabs__sidebar');
    if (!sidebar) return;

    var root = sidebar.closest('.bnb-mm-tabs');
    if (!root) return;

    // If the cursor is still inside the same tabbed mega menu (either
    // back in the sidebar or moving into the panel area), keep the
    // current preview — don't reset.
    if (e.relatedTarget && root.contains(e.relatedTarget)) return;

    lastHoverByRoot.delete(root);
    showPanelByIndex(root, 0);
  });

  // ---------- positioning -------------------------------------------

  // Left-align each tabbed mega menu's left edge with the leftmost edge
  // of the header's main navigation (where "Cigars" / first menu link
  // starts), instead of being centered in the viewport. The mega menu
  // is `position: absolute` with `inset-inline-start: 50%` from CSS;
  // we override that here with a calculated px offset so the menu
  // aligns with the nav row start. Re-runs on resize.
  function positionMegaMenusToNavStart() {
    var nav = document.querySelector('.header__menu');
    if (!nav) return;
    var navRect = nav.getBoundingClientRect();
    document.querySelectorAll('.mega-menu:has(.bnb-mm-tabs)').forEach(function (mega) {
      // Position relative to the mega's own offsetParent (its containing
      // block). Translate the nav's viewport-x into the offsetParent's
      // coordinate system.
      var parent = mega.offsetParent || document.documentElement;
      var parentRect = parent.getBoundingClientRect();
      var leftPx = navRect.left - parentRect.left;
      mega.style.setProperty('--bnb-mm-left', leftPx + 'px');
    });
  }

  window.addEventListener('resize', positionMegaMenusToNavStart);

  // ---------- initial setup -----------------------------------------

  function applyInitialActiveClasses() {
    document.querySelectorAll('.bnb-mm-tabs').forEach(function (root) {
      updateActiveClasses(root, 0);
    });
  }

  function observeMegaMenuOpens() {
    document.querySelectorAll('details[is="details-mega"]').forEach(function (details) {
      var observer = new MutationObserver(function () {
        if (details.hasAttribute('open')) {
          requestAnimationFrame(function () {
            positionMegaMenusToNavStart();
            details.querySelectorAll('.bnb-mm-tabs').forEach(function (root) {
              var idx = lastHoverByRoot.get(root);
              if (typeof idx !== 'number') idx = 0;
              updateActiveClasses(root, idx);
            });
          });
        }
      });
      observer.observe(details, { attributes: true, attributeFilter: ['open'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyInitialActiveClasses();
      observeMegaMenuOpens();
      positionMegaMenusToNavStart();
    });
  } else {
    applyInitialActiveClasses();
    observeMegaMenuOpens();
    positionMegaMenusToNavStart();
  }
})();
